import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import DateRange from 'components/Form/DateRange';
import Text from 'components/Form/Text';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { SubstanceThcClearanceItemModel } from 'forms/sections/substance/thc/clearance/types';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonOnError, CommonRequired
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';

interface Props extends Partial<SubstanceThcClearanceItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  scrollIntoView?: CommonScrollIntoView
  onError?: CommonOnError
  required?: CommonRequired
  formType?: string
}

export default class ThcClearanceUse extends ValidationElement<Props> {
  update = (updateValues: Partial<SubstanceThcClearanceItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        Description: this.props.Description,
        InvolvementDates: this.props.InvolvementDates,
        EstimatedUse: this.props.EstimatedUse,
        Comments: this.props.Comments,
        HowManyTimes: this.props.HowManyTimes, // PVQOnly
        Reason: this.props.Reason, // PVQOnly
        ...updateValues,
      });
    }
  };

  updateHowManyTimes = (values: SubstanceThcClearanceItemModel['HowManyTimes']) => {
    this.update({ HowManyTimes: values });
  };

  updateReason = (values: SubstanceThcClearanceItemModel['Reason']) => {
    this.update({ Reason: values });
  };

  updateDescription = (values: SubstanceThcClearanceItemModel['Description']) => {
    this.update({ Description: values });
  };

  updateInvolvementDates = (values: SubstanceThcClearanceItemModel['InvolvementDates']) => {
    this.update({ InvolvementDates: values });
  };

  updateEstimatedUse = (values: SubstanceThcClearanceItemModel['EstimatedUse']) => {
    this.update({ EstimatedUse: values });
  };

  updateComments = (values: SubstanceThcClearanceItemModel['Comments']) => {
    this.update({ Comments: values });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div className="thc-clearance-use">
        <Field
          attribute={`${attribute}.InvolvementDates`}
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            {...this.props.InvolvementDates}
            attribute={`${attribute}.InvolvementDates`}
            className="involvement-dates"
            fromDateLabel={i18n.t('substance.pvq.thc.clearance.heading.involvementDatesFrom')}
            hideFromDay
            hideToDay
            minDateEqualTo
            name="InvolvementDates"
            onError={this.props.onError}
            onUpdate={this.updateInvolvementDates}
            required={this.props.required}
            toDateLabel={i18n.t('substance.pvq.thc.clearance.heading.involvementDatesTo')}
            usePvqFromDate
            usePvqToDate
            allowPresent={false}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.thc.clearance.heading.frequency')}
          attribute={`${attribute}.EstimatedUse`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            {...this.props.EstimatedUse}
            name="EstimatedUse"
            attribute={`${attribute}.EstimatedUse`}
            className="estimated-use"
            onUpdate={this.updateEstimatedUse}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.thc.clearance.heading.howManyTimes')}
          attribute={`${attribute}.HowManyTimes`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            {...this.props.HowManyTimes}
            name="HowManyTimes"
            attribute={`${attribute}.HowManyTimes`}
            className="how-many-times"
            onUpdate={this.updateHowManyTimes}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.thc.clearance.heading.reason')}
          attribute={`${attribute}.Reason`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.Reason}
            name="Reason"
            attribute={`${attribute}.Reason`}
            className="reason"
            onUpdate={this.updateReason}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

      </div>
    );
  }
}

ThcClearanceUse.defaultProps = {
  onError: (value, arr: ErrorCode[]) => arr,
  Comments: {},
  HowManyTimes: {}, // PVQOnly
  Reason: {}, // PVQOnly
};
